import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Upload, User, Calendar, Phone, MapPin, Camera, FileText, AlertCircle } from 'lucide-react';
import { COUNTRIES, getProvincesForCountry, getCitiesForProvince } from '../constants/countries';

function CompleteProfilePage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    country: ''  // No default - user selects their country
  });

  // Dynamic lists based on selected country/province
  const [availableProvinces, setAvailableProvinces] = useState([]);

  // File state
  const [files, setFiles] = useState({
    profilePicture: null,
    idFront: null,
    idBack: null,
    selfieWithId: null
  });

  const [previews, setPreviews] = useState({
    profilePicture: null,
    idFront: null,
    idBack: null,
    selfieWithId: null
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle cascading dropdowns
    if (name === 'country') {
      // When country changes, update provinces and reset province
      const provinces = getProvincesForCountry(value);
      setAvailableProvinces(provinces);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        province: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(`${fieldName} file size must be less than 5MB`);
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setError(`${fieldName} must be a JPEG or PNG image`);
        return;
      }

      setFiles(prev => ({ ...prev, [fieldName]: file }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => ({ ...prev, [fieldName]: reader.result }));
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const validateForm = () => {
    // Validate all required fields
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!files.profilePicture) {
      setError('Profile picture is required');
      return false;
    }
    if (!formData.gender) {
      setError('Gender is required');
      return false;
    }
    if (!formData.dateOfBirth) {
      setError('Date of birth is required');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (!formData.address.trim()) {
      setError('Address is required');
      return false;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return false;
    }
    if (!formData.province.trim()) {
      setError('Province is required');
      return false;
    }
    if (!formData.postalCode.trim()) {
      setError('Postal code is required');
      return false;
    }

    // Validate age (must be 18+)
    const birthDate = new Date(formData.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 18) {
      setError('You must be at least 18 years old to register');
      return false;
    }

    // Validate files
    if (!files.idFront) {
      setError('Government ID (front) is required');
      return false;
    }
    if (!files.idBack) {
      setError('Government ID (back) is required');
      return false;
    }
    if (!files.selfieWithId) {
      setError('Selfie with ID is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add fields with correct backend field names
      submitData.append('profileFor', 'Self'); // Default value
      submitData.append('firstName', formData.firstName.trim());
      submitData.append('lastName', formData.lastName.trim());
      submitData.append('gender', formData.gender); // Use gender from form
      submitData.append('dob', formData.dateOfBirth);
      submitData.append('phone', formData.phone);
      submitData.append('street', formData.address);
      submitData.append('city', formData.city);
      submitData.append('province', formData.province);
      submitData.append('postalCode', formData.postalCode);
      submitData.append('country', formData.country);

      // Add files with correct field names
      submitData.append('profilePicture', files.profilePicture);
      submitData.append('idFront', files.idFront);
      submitData.append('idBack', files.idBack);
      submitData.append('selfieWithId', files.selfieWithId);

      // Submit to backend
      const response = await api.post('/identity-verification/submit-profile', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccess(true);
        // Refresh user data to get updated verification status
        await refreshUser();
        
        // Redirect to pending page after a short delay
        setTimeout(() => {
          navigate('/verification-pending');
        }, 2000);
      }
    } catch (err) {
      console.error('Profile submission error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to submit profile. Please try again.';
      
      // Handle the case where error detail is an object/array
      if (typeof errorMessage === 'object') {
        if (Array.isArray(errorMessage)) {
          setError(errorMessage.map(e => e.msg || JSON.stringify(e)).join(', '));
        } else {
          setError(JSON.stringify(errorMessage));
        }
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Submitted!</h2>
          <p className="text-gray-600 mb-4">
            Your verification documents have been submitted successfully. Our team will review them within 24-48 hours.
          </p>
          <p className="text-sm text-gray-500">
            Redirecting to status page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Complete Your Profile
          </h1>
          <p className="text-lg text-gray-600">
            Please provide your information and identity verification documents
          </p>
        </div>

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Error Alert */}
            {error && (
              <div className="bg-cream-100 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-navy-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-red-800">Error</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* Warning Box */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>Important:</strong> Your First Name, Last Name, Profile Picture, Country, and Province/State will be <strong>LOCKED after verification</strong> and cannot be changed without admin approval. Your city will remain private. Please ensure all information is accurate.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 1: Personal Information */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <User className="w-5 h-5 text-navy-500" />
                <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name * (Will be locked)
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="As per government ID"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name * (Will be locked)
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="As per government ID"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender *
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth * (Must be 18+ years old)
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    min={new Date(new Date().setFullYear(new Date().getFullYear() - 100)).toISOString().split('T')[0]}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">You must be at least 18 years old to register</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Address */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <MapPin className="w-5 h-5 text-navy-500" />
                <h2 className="text-xl font-semibold text-gray-900">Address</h2>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {/* Country Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country * (Will be locked)
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Country</option>
                    {COUNTRIES.map(country => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </select>
                </div>

                {/* Province/State Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Province/State * (Will be locked)
                  </label>
                  <select
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    required
                    disabled={!formData.country}
                  >
                    <option value="">Select Province/State</option>
                    {availableProvinces.map(province => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>
                  {!formData.country && (
                    <p className="text-xs text-gray-500 mt-1">Please select a country first</p>
                  )}
                </div>

                {/* City Text Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City * (For verification only - hidden from other users)
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter your city name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-yellow-600 mt-1">
                    🔒 City will NOT be visible to other users (privacy)
                  </p>
                </div>

                {/* Street Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Main Street, Apt 4B"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code *
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      placeholder="M5V 3A8"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>


            {/* Section 2.5: Profile Picture */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Camera className="w-5 h-5 text-navy-500" />
                <h2 className="text-xl font-semibold text-gray-900">Profile Picture (Will be locked)</h2>
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  id="profilePicture"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'profilePicture')}
                  className="hidden"
                />
                {previews.profilePicture ? (
                  <div className="space-y-4">
                    <img
                      src={previews.profilePicture}
                      alt="Profile"
                      className="w-32 h-32 object-cover rounded-full mx-auto border-4 border-red-100"
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById('profilePicture').click()}
                      className="text-navy-500 hover:text-navy-600 text-sm font-medium"
                    >
                      Change Photo
                    </button>
                  </div>
                ) : (
                  <label htmlFor="profilePicture" className="cursor-pointer">
                    <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-1">Click to upload your profile picture *</p>
                    <p className="text-xs text-gray-500">This will be your main profile photo (Max 20MB)</p>
                    <p className="text-xs text-yellow-600 mt-2 font-medium">⚠️ Cannot be changed after verification</p>
                  </label>
                )}
              </div>
            </div>

            {/* Section 3: Identity Documents */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <FileText className="w-5 h-5 text-navy-500" />
                <h2 className="text-xl font-semibold text-gray-900">Identity Verification Documents</h2>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Accepted IDs:</strong> Government-issued Driver's License, Passport, or National ID Card
                  <br />
                  <strong>Requirements:</strong> Clear, well-lit photos. All information must be readable.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* ID Front */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Government ID (Front) *
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={(e) => handleFileChange(e, 'idFront')}
                      className="hidden"
                      id="idFront"
                      required
                    />
                    <label
                      htmlFor="idFront"
                      className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-mint-500 transition-colors"
                    >
                      {previews.idFront ? (
                        <img src={previews.idFront} alt="ID Front" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Upload Front</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* ID Back */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Government ID (Back) *
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={(e) => handleFileChange(e, 'idBack')}
                      className="hidden"
                      id="idBack"
                      required
                    />
                    <label
                      htmlFor="idBack"
                      className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-mint-500 transition-colors"
                    >
                      {previews.idBack ? (
                        <img src={previews.idBack} alt="ID Back" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Upload Back</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* Selfie with ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selfie with ID *
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={(e) => handleFileChange(e, 'selfieWithId')}
                      className="hidden"
                      id="selfieWithId"
                      required
                    />
                    <label
                      htmlFor="selfieWithId"
                      className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-mint-500 transition-colors"
                    >
                      {previews.selfieWithId ? (
                        <img src={previews.selfieWithId} alt="Selfie" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <>
                          <Camera className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Upload Selfie</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-mint-500 hover:bg-mint-600 disabled:bg-gray-400 text-white font-semibold py-3 px-12 rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </span>
                ) : (
                  'Submit for Verification'
                )}
              </button>
            </div>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center text-sm text-gray-500">
          <p>Need help? Contact us at quboolme@gmail.com</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompleteProfilePage;